import { useId } from 'react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import TagsInput from '@/components/ui/tags-input'
import type { EffectivePolicyField, PolicyFieldDescriptor, PolicyFieldValue } from '@/lib/api/botSettings'
import { SOURCE_LABELS, formatPlatformValue, resolveDraftSource } from './policyFormUtils'

interface PolicyFieldProps {
  descriptor: PolicyFieldDescriptor
  /** Lo que el tenant tiene en su borrador (null si no escribió nada). */
  value: PolicyFieldValue | null
  /** Valor efectivo que el servidor devolvió en la última carga. */
  effective: EffectivePolicyField | undefined
  onChange: (value: PolicyFieldValue | null) => void
}

const SELECT_CLASSES =
  'flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#075E54] focus-visible:ring-offset-2'

export default function PolicyField({ descriptor, value, effective, onChange }: PolicyFieldProps) {
  const fieldId = useId()
  const helpId = `${fieldId}-help`
  const source = resolveDraftSource(value, effective)
  const platformText = formatPlatformValue(effective?.platformValue)
  const label = descriptor.unit ? `${descriptor.label} (${descriptor.unit})` : descriptor.label

  const control = (() => {
    switch (descriptor.type) {
      case 'boolean': {
        const checked = value ?? (effective?.platformValue === true)
        return (
          <Switch
            checked={checked === true}
            onChange={(e) => onChange(e.target.checked)}
            label={descriptor.label}
            aria-describedby={helpId}
          />
        )
      }
      case 'number':
        return (
          <Input
            id={fieldId}
            type="number"
            inputMode="decimal"
            min={descriptor.min}
            max={descriptor.max}
            value={typeof value === 'number' ? value : ''}
            placeholder={platformText}
            aria-describedby={helpId}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        )
      case 'select':
        return (
          <select
            id={fieldId}
            className={SELECT_CLASSES}
            value={typeof value === 'string' ? value : ''}
            aria-describedby={helpId}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">{platformText ? 'Usar el valor sugerido' : 'Sin definir'}</option>
            {descriptor.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
      case 'tags':
        return (
          <TagsInput
            id={fieldId}
            tags={Array.isArray(value) ? value : []}
            maxItems={descriptor.maxItems}
            placeholder="Escribe y presiona Enter..."
            onChange={(tags) => onChange(tags.length === 0 ? null : tags)}
          />
        )
      default:
        return (
          <Input
            id={fieldId}
            type="text"
            maxLength={descriptor.maxLength}
            value={typeof value === 'string' ? value : ''}
            placeholder={platformText}
            aria-describedby={helpId}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          />
        )
    }
  })()

  return (
    <div className="space-y-1">
      {descriptor.type !== 'boolean' && (
        <label htmlFor={fieldId} className="block text-xs font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      {control}
      <p id={helpId} className="text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-medium">{SOURCE_LABELS[source]}</span>
        {descriptor.helperText ? ` · ${descriptor.helperText}` : ''}
      </p>
    </div>
  )
}
